import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface UserCredits {
  credits_remaining: number;
  credits_total: number;
  subscription_tier: string;
  credits_reset_date: string | null;
}

export const useCredits = () => {
  const { user, isAdmin } = useAuth();
  const { toast } = useToast();
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCredits = useCallback(async () => {
    if (!user) {
      setCredits(null);
      setLoading(false);
      return;
    }

    // Admins get infinite credits — no need to fetch
    if (isAdmin) {
      setCredits({
        credits_remaining: 999999,
        credits_total: 999999,
        subscription_tier: 'Enterprise',
        credits_reset_date: null,
      });
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_credits')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching credits:', error);
        return;
      }

      if (data) {
        setCredits(data);
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  const deductCredits = useCallback(async (amount: number, feature: string, description?: string) => {
    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to use AI features.',
        variant: 'destructive',
      });
      return { success: false, error: 'NOT_AUTHENTICATED' };
    }

    // Admins bypass credit deduction entirely
    if (isAdmin) {
      return { success: true, credits_remaining: 999999, credits_deducted: 0 };
    }

    try {
      const { data, error } = await supabase.rpc('deduct_credits', {
        p_user_id: user.id,
        p_amount: amount,
        p_feature: feature,
        p_description: description || `Used ${feature}`
      });

      if (error) throw error;

      const result = data as any;
      if (result && !result.success) {
        if (result.error === 'INSUFFICIENT_CREDITS') {
          toast({
            title: '⚠️ Insufficient Credits',
            description: `You need ${result.credits_needed} credits but only have ${result.credits_remaining}. Please purchase more credits.`,
            variant: 'destructive',
          });
        }
        return result;
      }

      // Refresh credits
      await fetchCredits();
      return data;
    } catch (error) {
      console.error('Error deducting credits:', error);
      toast({
        title: 'Error',
        description: 'Failed to process credits. Please try again.',
        variant: 'destructive',
      });
      return { success: false, error: 'UNKNOWN_ERROR' };
    }
  }, [user, isAdmin, toast, fetchCredits]);

  return {
    credits,
    loading,
    refetch: fetchCredits,
    deductCredits
  };
};
